import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import LogOutIcon from 'icons/icon-log-out.svg';
import SupportIcon from 'icons/icon-support.svg';
import TrainingProfileIcon from 'icons/icon-training-profile.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useModal from 'components/hooks/useModal';
import useOutsideClick from 'components/hooks/useOutsideClick';

import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import PersonnelSupportModal from 'components/userBar/components/PersonnelSupportModal';

import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function UserMenu({
  isProcoreSession,
  isTrainingProfileNavigationVisible,
  lineManager,
  personnelId,
  personnelProfilePhoto,
}) {
  const breadBoard = useBreadBoard();

  const {
    user: {
      attributes: { accessType, firstName, lastName },
    },
  } = useCurrentActor();

  const ref = useRef();

  const [isSupportModalOpen, , openSupportModal, closeSupportModal] =
    useModal(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { t: i18nTCommon } = useTranslation('common');

  const handleTrainingProfileClick = () => {
    window.location.href = `/personnel/${personnelId}`;
  };

  const handleSupportClick = () => {
    if (accessType === 'personnel') {
      openSupportModal();
    } else {
      window.open('https://intercom.help/hands-hq', '_blank');
    }
  };

  const handleLogOutClick = () => {
    axios
      .delete(
        isProcoreSession ?
          '/users/integration_sessions/destroy'
        : '/users/sign_out',
      )
      .then(() => {
        window.location.href = '/users/sign_in';
      })
      .catch(breadBoard.addInedibleToast);
  };

  const toggleMenu = () => setIsDropdownOpen((prevState) => !prevState);

  useOutsideClick(() => {
    setIsDropdownOpen(false);
  }, [ref]);

  const initials = `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`;

  return (
    <>
      <div className='tw-relative' ref={ref}>
        <div
          className={classNames(
            'tw-flex tw-h-8 tw-w-14 tw-cursor-pointer tw-items-center tw-rounded-[20px] hover:tw-bg-grey-800',
            isDropdownOpen && 'tw-bg-grey-800',
          )}
          data-element-name='user-menu-toggle'
          onClick={toggleMenu}
        >
          <ProfilePhotoDisplay
            componentSize='sm'
            photoUrl={personnelProfilePhoto?.links?.croppedUrl}
            profileInitials={initials}
          />

          <div className='tw-flex tw-h-7 tw-w-5 tw-items-center tw-justify-center'>
            {isDropdownOpen ?
              <ChevronUpIcon
                className='[&_polygon]:tw-fill-white'
                height={20}
                width={20}
              />
            : <ChevronDownIcon
                className='[&_polygon]:tw-fill-white'
                height={20}
                width={20}
              />
            }
          </div>
        </div>
        {isDropdownOpen && (
          <div
            className='tw-absolute tw-right-0 tw-top-9 tw-z-[9999] tw-w-60 tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-shadow-lg'
            data-element-name='user-menu-dropdown'
          >
            <div className='tw-flex tw-h-32 tw-w-60 tw-flex-col tw-items-center tw-justify-center tw-p-3'>
              <ProfilePhotoDisplay
                componentSize='md'
                photoUrl={personnelProfilePhoto?.links?.croppedUrl}
                profileInitials={initials}
              />
              <div className='tw-text-l tw-font-semibold tw-text-grey-900'>
                {personDisplayName({ attributes: { firstName, lastName } })}
              </div>
            </div>
            <div className='tw-flex tw-w-60 tw-flex-col' onClick={toggleMenu}>
              {isTrainingProfileNavigationVisible && (
                <div
                  className='tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-p-3 tw-font-medium tw-text-grey-900 hover:tw-bg-blue-025'
                  onClick={
                    isTrainingProfileNavigationVisible ?
                      handleTrainingProfileClick
                    : undefined
                  }
                >
                  <TrainingProfileIcon
                    className='tw-mr-2 [&_polygon]:tw-fill-grey-900'
                    height={20}
                    width={20}
                  />
                  {i18nTCommon('training.trainingProfile')}
                </div>
              )}
              <div
                className='tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-p-3 tw-font-medium tw-text-grey-900 hover:tw-bg-blue-025'
                onClick={handleSupportClick}
              >
                <SupportIcon
                  className='tw-mr-2 [&_polygon]:tw-fill-grey-900'
                  height={20}
                  width={20}
                />
                {i18nTCommon('shared.support')}
              </div>
              <div
                className='tw-flex tw-cursor-pointer tw-items-center tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100 tw-p-3 tw-font-medium tw-text-grey-900 hover:tw-bg-blue-025'
                onClick={handleLogOutClick}
              >
                <LogOutIcon
                  className='tw-mr-2 [&_polygon]:tw-fill-grey-900'
                  height={20}
                  width={20}
                />
                {i18nTCommon('shared.logOut')}
              </div>
            </div>
          </div>
        )}
      </div>
      {accessType === 'personnel' && (
        <PersonnelSupportModal
          isOpen={isSupportModalOpen}
          lineManager={lineManager}
          onClose={closeSupportModal}
        />
      )}
    </>
  );
}

UserMenu.propTypes = {
  isProcoreSession: PropTypes.bool.isRequired,
  isTrainingProfileNavigationVisible: PropTypes.bool.isRequired,
  lineManager: resourceShape('personnel'),
  personnelId: PropTypes.string,
  personnelProfilePhoto: resourceShape('profilePhoto'),
};
